import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { request } from 'graphql-request';
import useGraphqlQuery from '../../../hooks/useGraphqlQuery';
import useGraphqlRequestParams from '../../../hooks/useGraphqlRequestParams';
import { LINEAGE_ASSET_COLUMNS } from '../../../constants/queryKeys';
import { getAssetColumnsQuery } from '../api';
import type { ApiLineageNode } from '../types';
import { isRestrictedApiLineageNode } from '../utils/lineageApiParsing';
import type { AssetDisplayType, ModelArtifactType } from '../../../types/types';

type AssetColumnsQueryResponse = {
  asset: {
    id: string;
    columns: Array<{
      id: string;
      parentId?: string;
      level?: string;
      type: string;
      name: string;
      artifactType: ApiLineageNode['artifactType'];
      displayType: ApiLineageNode['displayType'];
      tags: string[];
      owners: string[];
      path?: string;
      displayName?: string;
    }>;
  };
};

const mapAssetColumnsResponse = (
  data: AssetColumnsQueryResponse,
): ApiLineageNode[] =>
  data.asset.columns.map((column) => ({
    id: column.id,
    isRestricted: isRestrictedApiLineageNode({ __typename: 'ColumnAsset' }),
    type: column.artifactType as ModelArtifactType,
    displayType: column.displayType as AssetDisplayType,
    artifactType: column.artifactType as ModelArtifactType,
    parent: column.parentId,
    level: column.level as ApiLineageNode['level'],
    label: column.displayName || column.name,
    tags: column.tags,
    owners: column.owners,
    path: column.path,
  }));

type UseAssetColumnsProps = {
  assetId?: string;
  enabled?: boolean;
};

const useAssetColumns = ({
  assetId,
  enabled = false,
}: UseAssetColumnsProps) => {
  const { data, isFetching, isError } =
    useGraphqlQuery<AssetColumnsQueryResponse>({
      queryKey: [LINEAGE_ASSET_COLUMNS, assetId],
      query: getAssetColumnsQuery,
      variables: { assetId: assetId ?? '' },
      enabled: enabled && !!assetId,
    });

  const columns = data ? mapAssetColumnsResponse(data) : undefined;

  return {
    columns,
    isFetching,
    isError,
  };
};

export const useFetchAssetColumns = () => {
  const queryClient = useQueryClient();
  const { accessToken, gqlEndpoint } = useGraphqlRequestParams({});

  const fetchAssetColumns = useCallback(
    async (assetId: string): Promise<ApiLineageNode[]> => {
      const data = await queryClient.fetchQuery({
        queryKey: [LINEAGE_ASSET_COLUMNS, assetId],
        queryFn: () =>
          request<AssetColumnsQueryResponse>(
            gqlEndpoint,
            getAssetColumnsQuery,
            { assetId },
            { authorization: `Bearer ${accessToken}` },
          ),
      });
      return mapAssetColumnsResponse(data);
    },
    [accessToken, gqlEndpoint, queryClient],
  );

  return { fetchAssetColumns };
};

export default useAssetColumns;
